import { Pipe, PipeTransform } from '@angular/core';
import { parse, parseInline } from 'marked';

/**
 * How {@link NovaMarkdownPipe} renders: `block` produces paragraphs and lists for
 * standalone prose; `inline` produces only emphasis, code and links, for text placed
 * inside an element that already provides the block, such as a list item.
 */
export type NovaMarkdownMode = 'block' | 'inline';

const MARKED_OPTIONS = { async: false, gfm: true, breaks: true } as const;

/**
 * Renders agent markdown (follow-up replies, summaries) to HTML. The result is
 * bound via plain `[innerHTML]`, so Angular's DomSanitizer still strips anything
 * unsafe — we deliberately do NOT bypass sanitization for LLM output.
 */
@Pipe({ name: 'novaMarkdown', standalone: true })
export class NovaMarkdownPipe implements PipeTransform {
  /**
   * @param value Markdown written by the agent.
   * @param mode `block` for standalone prose, `inline` for text inside an existing element.
   * @returns HTML for a sanitized `[innerHTML]` binding; an empty string when there is
   * no value; the value unchanged when it cannot be parsed.
   */
  transform(
    value: string | null | undefined,
    mode: NovaMarkdownMode = 'block',
  ): string {
    if (!value) return '';
    try {
      return mode === 'inline'
        ? parseInline(value, MARKED_OPTIONS)
        : parse(value, MARKED_OPTIONS);
    } catch {
      return value;
    }
  }
}
